import Component from '@glimmer/component';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import Select from 'tio-reimagined/components/select';
import type IntlService from 'ember-intl/services/intl';
import type PersonModel from 'tio-data/models/person';
import type PostalAddressModel from 'tio-data/models/postal-address';
import type {
  LrapDemographicsConfig,
  LrapCustomDemographicQuestion,
} from 'tio-data/models/company-setting';

type CoreDemographicField = 'gender' | 'race' | 'ethnicity' | 'veteranStatus';

interface ContactInfoSignature {
  Args: {
    person: PersonModel;
    confirmed: boolean;
    onConfirm: (confirmed: boolean) => void;
    config: LrapDemographicsConfig | undefined;
    selectedGender: string | null;
    selectedRace: string | null;
    selectedEthnicity: string | null;
    selectedVeteranStatus: string | null;
    onDemographicChange: (field: CoreDemographicField, value: string) => void;
    additionalDemographics: Record<string, string>;
    onAdditionalDemographicChange: (key: string, value: string) => void;
  };
}

export default class LrapEnrollmentContactInfo extends Component<ContactInfoSignature> {
  @service declare intl: IntlService;

  get primaryEmail() {
    return this.args.person.primaryEmail?.email;
  }

  get primaryPostalAddress(): PostalAddressModel | undefined {
    return this.args.person.postalAddresses.find((a) => a.isActive);
  }

  get additionalQuestions(): LrapCustomDemographicQuestion[] {
    return this.args.config?.additionalQuestions ?? [];
  }

  get genderOptions() {
    return [
      { key: 'MALE', label: this.intl.t('lrap.enrollment.demographics.gender_male') },
      { key: 'FEMALE', label: this.intl.t('lrap.enrollment.demographics.gender_female') },
      { key: 'NON_BINARY', label: this.intl.t('lrap.enrollment.demographics.gender_non_binary') },
      {
        key: 'SELF_DESCRIBE',
        label: this.intl.t('lrap.enrollment.demographics.gender_self_describe'),
      },
      { key: 'NOT_DISCLOSED', label: this.intl.t('lrap.enrollment.demographics.not_disclosed') },
    ];
  }

  get raceOptions() {
    return [
      {
        key: 'AMERICAN_INDIAN_ALASKA_NATIVE',
        label: this.intl.t('lrap.enrollment.demographics.race_aian'),
      },
      { key: 'ASIAN', label: this.intl.t('lrap.enrollment.demographics.race_asian') },
      {
        key: 'BLACK_AFRICAN_AMERICAN',
        label: this.intl.t('lrap.enrollment.demographics.race_black'),
      },
      {
        key: 'NATIVE_HAWAIIAN_PACIFIC_ISLANDER',
        label: this.intl.t('lrap.enrollment.demographics.race_nhpi'),
      },
      { key: 'WHITE', label: this.intl.t('lrap.enrollment.demographics.race_white') },
      { key: 'TWO_OR_MORE', label: this.intl.t('lrap.enrollment.demographics.race_two_or_more') },
      { key: 'NOT_DISCLOSED', label: this.intl.t('lrap.enrollment.demographics.not_disclosed') },
    ];
  }

  get ethnicityOptions() {
    return [
      {
        key: 'HISPANIC_LATINO',
        label: this.intl.t('lrap.enrollment.demographics.ethnicity_hispanic'),
      },
      {
        key: 'NOT_HISPANIC_LATINO',
        label: this.intl.t('lrap.enrollment.demographics.ethnicity_not_hispanic'),
      },
      { key: 'NOT_DISCLOSED', label: this.intl.t('lrap.enrollment.demographics.not_disclosed') },
    ];
  }

  get veteranStatusOptions() {
    return [
      { key: 'VETERAN', label: this.intl.t('lrap.enrollment.demographics.veteran_yes') },
      { key: 'NON_VETERAN', label: this.intl.t('lrap.enrollment.demographics.veteran_no') },
      { key: 'NOT_DISCLOSED', label: this.intl.t('lrap.enrollment.demographics.not_disclosed') },
    ];
  }

  toggleConfirmed = (event: Event) => {
    this.args.onConfirm((event.target as HTMLInputElement).checked);
  };

  getAdditionalDemographic = (key: string): string | null =>
    this.args.additionalDemographics[key] ?? null;

  onGenderChange = (value: string) => this.args.onDemographicChange('gender', value);
  onRaceChange = (value: string) => this.args.onDemographicChange('race', value);
  onEthnicityChange = (value: string) => this.args.onDemographicChange('ethnicity', value);
  onVeteranStatusChange = (value: string) => this.args.onDemographicChange('veteranStatus', value);

  <template>
    <div class="tio-col" data-test-lrap-contact-step>
      <h2 class="app-section-title">{{t "lrap.enrollment.contact_info.title"}}</h2>
      <p class="tio-lrap-intro">{{t "lrap.enrollment.contact_info.intro"}}</p>

      <div class="tio-col tio-lrap-card">
        <div class="tio-col-tight">
          <p class="tio-lrap-field-label">
            {{t "lrap.enrollment.contact_info.email_label"}}
          </p>
          <p>{{if this.primaryEmail this.primaryEmail "—"}}</p>
        </div>

        {{#if this.primaryPostalAddress}}
          <div class="tio-col-tight">
            <p class="tio-lrap-field-label">
              {{t "lrap.enrollment.contact_info.address_label"}}
            </p>
            <address class="tio-lrap-address">
              <p>{{this.primaryPostalAddress.addressLine1}}</p>
              {{#if this.primaryPostalAddress.addressLine2}}
                <p>{{this.primaryPostalAddress.addressLine2}}</p>
              {{/if}}
              <p>
                {{this.primaryPostalAddress.city}},
                {{this.primaryPostalAddress.state}}
                {{this.primaryPostalAddress.postalCode}}
              </p>
            </address>
          </div>
        {{/if}}

        <p class="tio-lrap-hint">{{t "lrap.enrollment.contact_info.update_hint"}}</p>
      </div>

      <label class="tio-row-top-tight tio-lrap-confirm" data-test-contact-confirm-label>
        <input
          type="checkbox"
          checked={{@confirmed}}
          data-test-contact-confirm-checkbox
          {{on "change" this.toggleConfirmed}}
        />
        <span class="tio-lrap-confirm__text">{{t
            "lrap.enrollment.contact_info.confirm_label"
          }}</span>
      </label>

      <div class="tio-col" data-test-lrap-demographics-section>
        <h3 class="app-subsection-title">{{t "lrap.enrollment.demographics.title"}}</h3>
        <p class="tio-lrap-intro">{{t "lrap.enrollment.demographics.intro"}}</p>

        <div class="tio-col">
          <Select
            @label={{t "lrap.enrollment.demographics.gender_label"}}
            @items={{this.genderOptions}}
            @selected={{@selectedGender}}
            @onChange={{this.onGenderChange}}
            @placeholder={{t "lrap.enrollment.demographics.select_placeholder"}}
            data-test-gender-select
          />
          <Select
            @label={{t "lrap.enrollment.demographics.veteran_status_label"}}
            @items={{this.veteranStatusOptions}}
            @selected={{@selectedVeteranStatus}}
            @onChange={{this.onVeteranStatusChange}}
            @placeholder={{t "lrap.enrollment.demographics.select_placeholder"}}
            data-test-veteran-status-select
          />
          <Select
            @label={{t "lrap.enrollment.demographics.race_label"}}
            @items={{this.raceOptions}}
            @selected={{@selectedRace}}
            @onChange={{this.onRaceChange}}
            @placeholder={{t "lrap.enrollment.demographics.select_placeholder"}}
            data-test-race-select
          />
          <Select
            @label={{t "lrap.enrollment.demographics.ethnicity_label"}}
            @items={{this.ethnicityOptions}}
            @selected={{@selectedEthnicity}}
            @onChange={{this.onEthnicityChange}}
            @placeholder={{t "lrap.enrollment.demographics.select_placeholder"}}
            data-test-ethnicity-select
          />

          {{#each this.additionalQuestions as |question|}}
            <Select
              @label={{question.label}}
              @items={{question.options}}
              @selected={{this.getAdditionalDemographic question.key}}
              @onChange={{fn @onAdditionalDemographicChange question.key}}
              @placeholder={{t "lrap.enrollment.demographics.select_placeholder"}}
              data-test-additional-demographic-select
            />
          {{/each}}
        </div>

        <p class="tio-lrap-hint">{{t "lrap.enrollment.demographics.optional_note"}}</p>
      </div>
    </div>
  </template>
}
